import { useUIStore } from '@/stores/uiStore';

/** Formats a date string for CSV export: locale-aware, consistent across all pages. */
export function formatExportDate(d: string | null | undefined): string {
  if (!d) return '';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '' : date.toLocaleString();
}

interface CsvOptions {
  /** Page/report name for metadata header (e.g. "Nodes", "Inventory") */
  report?: string;
  /** Whether to include org name and timestamp metadata rows above data */
  includeMetadata?: boolean;
  /** Override the title line (default: "Neeve Insights — OrgName") */
  title?: string;
}

function escapeCell(val: unknown): string {
  if (val === null || val === undefined) return '';
  const str = val instanceof Date ? val.toISOString() : String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generates a CSV string from an array of objects and triggers download.
 * Includes metadata headers (org name, report name, timestamp) by default.
 */
export function downloadCsv(
  data: Record<string, unknown>[],
  filename: string,
  options: CsvOptions = {}
) {
  if (data.length === 0) return;

  const { report, includeMetadata = true, title } = options;
  const headers = Object.keys(data[0]);
  const rows = data.map((row) => headers.map((h) => escapeCell(row[h])).join(','));

  const lines: string[] = [];

  if (includeMetadata) {
    if (title) {
      lines.push(escapeCell(title));
    } else {
      const session = useUIStore.getState().session;
      const orgName = session?.tenant?.name || 'Unknown Organization';
      const isDemo = session?.isDemo === true;
      lines.push(escapeCell(`Neeve Insights${isDemo ? ' (Demo Mode)' : ''} — ${orgName}`));
    }
    if (report) lines.push(escapeCell(`Report: ${report}`));
    lines.push(escapeCell(`Exported: ${new Date().toLocaleString()}`));
    lines.push(''); // blank separator row
  }

  lines.push(headers.join(','));
  lines.push(...rows);

  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
